import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';
import { strings } from './strings';
import ProcessPage from './ProcessPage';

const HeroSection = styled.section`
  background: linear-gradient(to bottom right, #fff7ed, #fed7aa);
  padding: 5rem 0;
  direction: rtl;
`;

const SectionContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }
  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const HeroContent = styled.div`
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 1.5rem;
  white-space: pre-line;

  @media (min-width: 768px) {
    font-size: 3.75rem;
  }
`;

const HeroHighlight = styled.span`
  color: #ea580c;
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  color: #374151;
  margin-bottom: 2rem;
  max-width: 48rem;
  margin-left: auto;
  margin-right: auto;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
  
  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const PrimaryButton = styled.button`
  background-color: #ea580c;
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #c2410c;
  }
`;

const SecondaryButton = styled.button`
  border: 2px solid #ea580c;
  color: #ea580c;
  padding: 0.75rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #ea580c;
    color: white;
  }
`;

const ContentSection = styled.section`
  padding: 6rem 0;
  direction: rtl;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 30%, #cbd5e1 70%, #94a3b8 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(234, 88, 12, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(251, 146, 60, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(234, 88, 12, 0.02) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const ContentContainer = styled.div`
  max-width: 64rem;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }
  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  gap: 3rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(1, 1fr);
    gap: 2rem;
  }
  
  @media (min-width: 1024px) {
    gap: 2rem;
  }
`;

const ContentCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  opacity: 0;
  transform: translateY(50px);
  transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  ${props => props.visible && `
    opacity: 1;
    transform: translateY(0);
  `}
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 1rem;  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition-delay: 0.2s;
  
  ${props => props.visible && `
    opacity: 1;
    transform: translateY(0);
  `}
  
  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    right: 0;
    width: 0;
    height: 3px;
    background: linear-gradient(90deg, #ea580c, #f97316);
    transition: width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transition-delay: 0.8s;
  }
  
  ${props => props.visible && `
    &::after {
      width: 4rem;
    }
  `}
`;

const CardText = styled.p`
  color: #374151;
  line-height: 1.625;
  white-space: pre-line; /* Preserve line breaks */
`;

const CardContent = styled.div`
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(20px);
  border-radius: 2rem;
  padding: 2.5rem;
  box-shadow: 
    0 20px 40px -12px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transform: perspective(1000px) rotateX(0deg) rotateY(0deg);
`;

const HighlightSection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%);
  direction: rtl;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 20%, rgba(234, 88, 12, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 70% 80%, rgba(234, 88, 12, 0.03) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const HighlightContainer = styled.div`
  position: relative;
  max-width: 65rem;
  margin: 0 auto;
  padding: 0 2rem;
  z-index: 1;
`;

const HighlightContent = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 2rem;
  padding: 4rem 3rem;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  @media (max-width: 768px) {
    padding: 3rem 2rem;
    border-radius: 1.5rem;
  }
`;

const HighlightSentence = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 2rem;
  line-height: 1.3;
  letter-spacing: -0.02em;
  text-align: center;
  position: relative;
  
  &:not(:last-of-type)::after {
    content: '';
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    width: 2rem;
    height: 2px;
    background: linear-gradient(90deg, transparent, #cbd5e1, transparent);
  }
  
  @media (min-width: 768px) {
    font-size: 2.125rem;
    margin-bottom: 2.5rem;
    
    &:not(:last-of-type)::after {
      bottom: -1.25rem;
      width: 3rem;
    }
  }
  
  @media (min-width: 1024px) {
    font-size: 1.8rem;
  }
`;

const HighlightClosing = styled.div`
  font-size: 2.25rem;
  font-weight: 800;
  background: linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
  margin-top: 3rem;
  line-height: 1.2;
  letter-spacing: -0.03em;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -1.5rem;
    left: 50%;
    transform: translateX(-50%);
    width: 4rem;
    height: 3px;
    background: linear-gradient(90deg, #ea580c, #f97316, #fb923c);
    border-radius: 2px;
  }
  
  @media (min-width: 768px) {
    font-size: 2.75rem;
    margin-top: 3.5rem;
    
    &::before {
      width: 5rem;
      top: -2rem;
    }
  }
  
  @media (min-width: 1024px) {
    font-size: 3.25rem;
  }
`;

const ContactSection = styled.section`
  background-color: #ea580c;
  padding: 4rem 0;
  direction: rtl;
`;

const ContactContainer = styled.div`
  max-width: 42rem;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }
  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const ContactHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const ContactTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: bold;
  color: white;
  margin-bottom: 1rem;
`;

const ContactSubtitle = styled.p`
  color: #fed7aa;
`;

const ContactForm = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 2rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const FormGroup = styled.div``;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

// Add loading spinner for homepage
const HomeLoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-left: 0.5rem;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// Update existing Input and SubmitButton for disabled states
const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #ea580c;
    box-shadow: 0 0 0 2px rgba(234, 88, 12, 0.2);
  }
  
  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: #ea580c;
  color: white;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: #c2410c;
  }
  
  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
`;

const PageSection = styled.section`
  padding: 4rem 0;
  direction: rtl;
`;

const PageContainer = styled.div`
  max-width: 64rem;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }
  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.25rem;
  color: #374151;
  max-width: 48rem;
  margin: 0 auto;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  gap: 3rem;
  align-items: center;
  margin-bottom: 4rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 24rem;
  background: linear-gradient(to bottom right, #fed7aa, #fdba74);
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: #ea580c;
`;

const TwoColumnContentGrid = styled.div`
  display: grid;
  gap: 2rem;
  margin-bottom: 4rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const CredentialsCard = styled.div`
  background-color: white;
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border-right: 4px solid #ea580c;
`;

const SpecializationsCard = styled.div`
  background-color: #fff7ed;
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const CardSubtitle = styled.h3`
  font-size: 1.25rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 0.75rem;
`;

const List = styled.ul`
  color: #374151;
  
  li {
    margin-bottom: 0.5rem;
  }
`;

const MissionCard = styled.div`
  background-color: #ea580c;
  color: white;
  padding: 2rem;
  border-radius: 0.5rem;
`;

const MissionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
`;

const MissionText = styled.p`
  color: #fed7aa;
  line-height: 1.625;
`;

const HomePage = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [visibleCards, setVisibleCards] = useState({});
  const contentRef = useRef(null);
  const contactRef = useRef(null);
  const cardRefs = useRef([]);

  // Intersection Observer for scroll animations
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cardIndex = cardRefs.current.indexOf(entry.target);
            if (cardIndex !== -1) {
              setVisibleCards(prev => ({ ...prev, [cardIndex]: true }));
            }
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async () => {
    if (!name || !email) {
      alert(strings.home.contact.fillAllFields);
      return;
    }

    setIsLoading(true);

    try {
      const templateParams = {
        from_name: name,
        from_email: email,
        from_phone: phone || 'לא צוין',
        message: `בקשה להתחלת מסע כושר מדף הבית. אנא צרו קשר לתיאום פגישת ייעוץ ראשונית.`,
        to_email: 'haggaimagen87@gmail.com'
      };

      await emailjs.send(
        'service_w3pkjf6',
        'template_lwf20nq',
        templateParams,
        'Sgy1Do0paUmA8QyPR'
      );

      alert(`${strings.home.contact.thankYouMessage} ${name}! ההודעה נשלחה בהצלחה. ${strings.home.contact.followUpMessage} ${email} ${strings.home.contact.soon}.`);
      setEmail('');
      setName('');
      setPhone('');
    } catch (error) {
      console.error('EmailJS Error:', error);
      alert('שגיאה בשליחת ההודעה. אנא נסה שוב או צור קשר טלפונית.');
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToContent = () => {
    contentRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

const contentCards = [
  {
    title: "למה שום דבר לא עובד?",
    content: [
      "אם אתה לא מצליח להחזיר את החיים שלך למסלול, לרדת במשקל ולחזור לכושר, זה לא בגלל שתוכנית האימון שלך לא מספיק טובה. בטח גם ניסית דיאטות כאלה ואחרות, אולי תפריט תזונה מותאם אישית."
    ],
    icon: "❌",
    reverse: false
  },
  {
    title: "התבנית החוזרת של כישלון",
    content: [
      "יכול להיות שכבר לקחת מאמן שהסביר לך ונתן לך תוכנית אימונים. שילמת למאמנים ולתזונאים הכי טובים שיש בשוק, ובכל זאת - בסוף חזרת לנקודת ההתחלה."
    ],
    icon: "🔄",
    reverse: true
  },
  {
    title: "השיטות הקיימות לא מתאימות לך",
    content: [
      "לא משנה כמה מוכשר המאמן שלקחת או מנוסה התזונאי שאמר לך מה לאכול, אם לא הצלחת להגיע או שכמעט הגעת אבל אתה לא מצליח לשמר את התוצאות, <strong>יכול להיות שהשיטות שיש לעולם להציע לך פשוט לא מתאימות לך</strong>."
    ],
    icon: "⚠️",
    reverse: false
  },
  {
    title: "זה לא \"זבנג וגמרנו\"",
    content: [
      "לפעמים שינוי קיצוני יכול להיות גדול עלינו. במקום לקדם אותנו, הוא מפעיל עלינו לחץ, פוגע לנו במוטיבציה וגורם לנו לוותר. להגיע למכון הכושר בפעם הראשונה בלי להכיר אף אחד, כשכולם שם נראים חזקים ומתאמנים כבר שנים - כנראה מרגיש יותר מדי מחוץ לאיזור הנוחות שלך."
    ],
    icon: "⚡",
    reverse: true
  },
  {
    title: "מעגל הסבל האינסופי",
    content: [
      "בחוגים או במכון הכושר יש לך תחושה שכולם מסתכלים עליך ושופטים אותך, מרגיש לך שאתה לא יודע מה אתה עושה. אתה מרגיש לא בנוח עם המשקל והמראה שלך, אבל גם לשנות את התזונה מקצה לקצה ביום אחד - להתחיל דיאטה - רק מרגיש כמו להיכנס ללופ אינסופי של סבל, ובסופו של דבר תמיד אתה חוזר לנקודת ההתחלה או גרוע מזה."
    ],
    icon: "🔁",
    reverse: false
  }
];
  return (
    <div>
      <HeroSection>
        <SectionContainer>
          <HeroContent>
            <HeroTitle>
              {strings.home.hero.title}
              <HeroHighlight> {strings.global.businessName}</HeroHighlight>
            </HeroTitle>
            <HeroSubtitle>
              {strings.home.hero.subtitle}
            </HeroSubtitle>
            <ButtonGroup>
              <PrimaryButton onClick={scrollToContact}>{strings.home.hero.primaryButton}</PrimaryButton>
              <SecondaryButton onClick={scrollToContent}>{strings.home.hero.secondaryButton}</SecondaryButton>
            </ButtonGroup>
          </HeroContent>
        </SectionContainer>
      </HeroSection>

      <HighlightSection>
        <ContentContainer>
          <HighlightContainer>
            <HighlightContent>
              {strings.home.highlight.sentences.map((sentence, index) => (
                <HighlightSentence key={index}>{sentence}</HighlightSentence>
              ))}
              <HighlightClosing>{strings.home.highlight.closing}</HighlightClosing>
            </HighlightContent>
          </HighlightContainer>
        </ContentContainer>
      </HighlightSection>

      <ContentSection ref={contentRef}>
        <ContentContainer>
          <ContentGrid>
            {contentCards.map((card, index) => (
              <ContentCard 
                key={index} 
                reverse={card.reverse}
                visible={visibleCards[index]}
                ref={el => cardRefs.current[index] = el}
              >
                <CardContent reverse={card.reverse}>
                  <CardTitle visible={visibleCards[index]}>{card.title}</CardTitle>
                  <CardText visible={visibleCards[index]}>
                    {card.content.map((paragraph, pIndex) => (
                      <p key={pIndex} dangerouslySetInnerHTML={{ __html: paragraph }} />
                    ))}
                  </CardText>
                </CardContent>
              </ContentCard>
            ))}
          </ContentGrid>
        </ContentContainer>
      </ContentSection>

      <ProcessPage />
      
      <PageSection>
        <PageContainer>
          <PageHeader>
            <PageTitle>חגי מגן - מאמן לאורח חיים בריא</PageTitle>
            <PageSubtitle>
              
            </PageSubtitle>
          </PageHeader>

          <TwoColumnGrid>
            <ImagePlaceholder>👤</ImagePlaceholder>
            <div>
              <CardTitle>השותף שלך לשינוי</CardTitle>
              <CardText style={{ marginBottom: '1rem' }}>
                עם למעלה מ-15 שנות ניסיון באימון כושר ואורח חיים, אני בוחר להקדיש את עצמי כדי לעזור 
                לאנשים לגלות את העצמי החזק, הבריא והבטוח ביותר שלהם. הגישה שלי משלבת מומחיות מדעית 
                עם אכפתיות אמיתית למסע הייחודי של כל לקוח.
              </CardText>
              <CardText>
                אני מאמין ששינוי אמיתי לא מתרחש רק בשינויים פיזיים—זה גם בניית ביטחון, 
                פיתוח חוסן ויצירת הרגלים מתמשכים המשפרים כל היבט של החיים שלנו.
              </CardText>
            </div>
          </TwoColumnGrid>

          <TwoColumnContentGrid>
            <CredentialsCard>
              <CardSubtitle>הסמכות וחינוך</CardSubtitle>
              <List>
                <li>מאמן כושר מוסמך</li>
                <li>הסמכה בתחום התזונה</li>
                <li>מומחה לשינוי התנהגותי</li>
              </List>
            </CredentialsCard>
            <SpecializationsCard>
              <CardSubtitle>התמחויות</CardSubtitle>
              <List>
                <li>ירידה במשקל ושינוי הרכב הגוף</li>
                <li>אימוני כוח ובניית שרירים</li>
                <li>אימון אורח חיים ויצירת הרגלים</li>
                <li>ניהול מתח ורווחה נפשית</li>
              </List>
            </SpecializationsCard>
          </TwoColumnContentGrid>

          <MissionCard>
            <MissionTitle>המשימה שלי</MissionTitle>
            <MissionText>
              לסייע ולתת את הכלים הנכונים לאנשים שמבינים שהגיע הזמן לעשות שינוי באורח החיים אבל הם לא יודעים איך ומאיפה להתחיל. 
              לעבוד על בניה של יכולת התמדה - לא בשינוי גדול ומיידי אלא צעד אחר צעד, לאסוף הצלחות קטנות, וביחד להחזיר את החיים למסלול הנכון. 
              זו לא עוד תוכנית אימון או תפריט תזונה - זה תהליך של שינוי פנימי שבו נלמד איך להפוך לאדם שלוקח אחריות מלאה על הבריאות הפיזית והנפשית שלו ומצליח להתמיד בזה לאורך זמן.
            </MissionText>
          </MissionCard>
        </PageContainer>
      </PageSection>
      
      <ContactSection ref={contactRef}>
        <ContactContainer>
          <ContactHeader>
            <ContactTitle>{strings.home.contact.title}</ContactTitle>
            <ContactSubtitle>{strings.home.contact.subtitle}</ContactSubtitle>
          </ContactHeader>
          <ContactForm>
            <FormGrid>
              <FormGroup>
                <Label htmlFor="name">{strings.home.contact.nameLabel}</Label>
                <Input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={strings.home.contact.namePlaceholder}
                  disabled={isLoading}
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="email">{strings.home.contact.emailLabel}</Label>
                <Input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={strings.home.contact.emailPlaceholder}
                  disabled={isLoading}
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="phone">מספר טלפון</Label>
                <Input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="050-"
                  disabled={isLoading}
                />
              </FormGroup>
              <SubmitButton onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? (
                  <>
                    שולח...
                    <HomeLoadingSpinner />
                  </>
                ) : (
                  strings.home.contact.submitButton
                )}
              </SubmitButton>
            </FormGrid>
          </ContactForm>
        </ContactContainer>
      </ContactSection>
    </div>
  );
};

export default HomePage;